import { useState, useRef } from "react";
import { IconButton, TextField, Paper, Typography } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";

interface Props {
  onSend: (text: string) => void;
  disabled?: boolean;
  isPolling?: boolean;
  pollingProgress?: number;
}

export default function ChatInput({
  onSend,
  disabled = false,
  isPolling = false,
}: Props) {
  const [text, setText] = useState("");
  const lastSentText = useRef<string>("");

  function handleSend() {
    if (!text.trim() || disabled || isPolling) return;

    if (text.trim() === lastSentText.current) {
      return;
    }

    lastSentText.current = text.trim();
    onSend(text.trim());
    setText("");
  }

  return (
    <Paper
      elevation={8}
      sx={{
        background:
          "linear-gradient(135deg, rgba(26, 49, 60, 0.95) 0%, rgba(4, 9, 10, 0.98) 100%)",
        backdropFilter: "blur(20px)",
        border: "2px solid rgba(226, 114, 91, 0.25)",
        borderRadius: { xs: "20px", md: "32px" },
        boxShadow:
          "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(226, 114, 91, 0.1)",
      }}
    >
      <div className="flex items-center gap-2 px-2 py-2 md:gap-4 md:px-6 md:py-5">
        <TextField
          fullWidth
          multiline
          slotProps={{
            htmlInput: {
              'data-test': 'chat-input',
            },
          }}
          maxRows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={
            isPolling
              ? "Aguarde, Dr. Weedy está pensando..."
              : "Descreva seus sintomas ou faça uma pergunta médica..."
          }
          variant="standard"
          disabled={disabled || isPolling}
          sx={{
            "& .MuiInput-underline:before": { display: "none" },
            "& .MuiInput-underline:after": { display: "none" },
            "& .MuiInputBase-input": {
              fontSize: { xs: "14px", md: "17px" },
              lineHeight: "1.6",
              padding: { xs: "8px 0", md: "12px 0" },
              color: "#ffffff",
              fontWeight: 500,
            },
            "& .MuiInputBase-input::placeholder": {
              color: isPolling
                ? "rgba(255,255,255,0.4)"
                : "rgba(255,255,255,0.5)",
              opacity: 1,
            },
          }}
        />

        <div className="flex gap-2 md:gap-3">
          <IconButton
            disabled={isPolling}
            sx={{
              borderRadius: { xs: "12px", md: "16px" },
              width: { xs: "36px", md: "52px" },
              height: { xs: "36px", md: "52px" },
              color: "rgba(255,255,255,0.6)",
              backgroundColor: "rgba(255,255,255,0.05)",
              "&:hover": {
                backgroundColor: "rgba(226, 114, 91, 0.15)",
                color: "#E2725B",
              },
              "&:disabled": {
                opacity: 0.3,
                color: "rgba(255,255,255,0.3)",
              },
            }}
          >
            <MicIcon sx={{ fontSize: { xs: "20px", md: "24px" } }} />
          </IconButton>

          <IconButton
            onClick={handleSend}
            data-test="send-button"
            disabled={!text.trim() || disabled || isPolling}
            sx={{
              borderRadius: { xs: "14px", md: "20px" },
              width: { xs: "40px", md: "56px" },
              height: { xs: "40px", md: "56px" },
              background:
                text.trim() && !disabled && !isPolling
                  ? "linear-gradient(135deg, #E2725B 0%, #CC3B2A 100%)"
                  : "rgba(255,255,255,0.1)",
              color: "#ffffff",
              border:
                text.trim() && !disabled && !isPolling
                  ? "2px solid rgba(226, 114, 91, 0.4)"
                  : "1px solid rgba(255,255,255,0.1)",
              boxShadow:
                text.trim() && !disabled && !isPolling
                  ? "0 8px 24px rgba(226, 114, 91, 0.4)"
                  : "none",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                transform:
                  text.trim() && !isPolling
                    ? "translateY(-2px) scale(1.05)"
                    : "none",
                boxShadow:
                  text.trim() && !isPolling
                    ? "0 12px 32px rgba(226, 114, 91, 0.5)"
                    : "none",
              },
              "&:disabled": {
                opacity: 0.4,
                color: "rgba(255,255,255,0.4)",
              },
            }}
          >
            <SendIcon sx={{ fontSize: { xs: "20px", md: "26px" } }} />
          </IconButton>
        </div>
      </div>

      <div className="px-2 pb-2 md:px-6 md:pb-4">
        <Typography
          variant="caption"
          sx={{
            color: "rgba(255,255,255,0.5)",
            fontSize: { xs: "11px", md: "12px" },
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          {isPolling
            ? "Processando sua consulta. Isso pode levar alguns minutos."
            : "Dr. Weedy pode cometer erros. Considere verificar informações importantes."}
        </Typography>
      </div>
    </Paper>
  );
}
